/**
 * Web Audio API Procedural Synthesizer for ChittiWin
 * 100% offline, zero network requests, zero external assets, instant loading.
 */

class SoundEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private isMuted: boolean = false;
  private volume: number = 0.75;
  private lastTickTime: number = 0;

  constructor() {
    // AudioContext will be initialized on first user interaction to satisfy browser autoplay policies
  }

  private initContext() {
    if (!this.ctx) {
      const AudioCtxClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioCtxClass) {
        this.ctx = new AudioCtxClass();
        this.masterGain = this.ctx.createGain();
        this.masterGain.gain.setValueAtTime(this.isMuted ? 0 : this.volume, this.ctx.currentTime);
        this.masterGain.connect(this.ctx.destination);
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
  }

  public setMuted(muted: boolean) {
    this.isMuted = muted;
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setValueAtTime(muted ? 0 : this.volume, this.ctx.currentTime);
    }
  }

  public setVolume(vol: number) {
    this.volume = Math.max(0, Math.min(1, vol));
    if (this.masterGain && this.ctx && !this.isMuted) {
      this.masterGain.gain.setValueAtTime(this.volume, this.ctx.currentTime);
    }
  }

  public getVolume(): number {
    return this.volume;
  }

  public getIsMuted(): boolean {
    return this.isMuted;
  }

  /**
   * Crisp mechanical peg tick
   * Pitch and intensity adapt dynamically to wheel rotation speed
   */
  public playTick(velocityRatio: number = 1) {
    if (this.isMuted) return;
    this.initContext();
    if (!this.ctx || !this.masterGain) return;

    const now = this.ctx.currentTime;
    // Throttle ticks to avoid audio buffer saturation at extreme speeds
    if (now - this.lastTickTime < 0.025) return;
    this.lastTickTime = now;

    // Pitch: faster = higher crisp pitch (800Hz - 1600Hz)
    const baseFreq = 850 + Math.min(velocityRatio * 600, 750);
    
    // High-pass filtered click oscillator
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();

    filter.type = 'highpass';
    filter.frequency.setValueAtTime(400, now);

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(baseFreq, now);
    osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.4, now + 0.035);

    // Sharp percussive envelope
    const tickVol = Math.min(0.4 + velocityRatio * 0.3, 0.7);
    gain.gain.setValueAtTime(tickVol, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.035);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.04);

    // Subtle wooden/plastic resonance body
    const subOsc = this.ctx.createOscillator();
    const subGain = this.ctx.createGain();
    subOsc.type = 'sine';
    subOsc.frequency.setValueAtTime(baseFreq * 0.5, now);
    subOsc.frequency.exponentialRampToValueAtTime(100, now + 0.03);

    subGain.gain.setValueAtTime(0.2 * tickVol, now);
    subGain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);

    subOsc.connect(subGain);
    subGain.connect(this.masterGain);

    subOsc.start(now);
    subOsc.stop(now + 0.035);
  }

  /**
   * Spin Start / Whoosh sound
   */
  public playSpinStart() {
    if (this.isMuted) return;
    this.initContext();
    if (!this.ctx || !this.masterGain) return;

    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(220, now);
    osc.frequency.exponentialRampToValueAtTime(660, now + 0.4);

    gain.gain.setValueAtTime(0.01, now);
    gain.gain.linearRampToValueAtTime(0.3, now + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.5);
  }

  /**
   * Mechanical stop clack
   */
  public playStopClack() {
    if (this.isMuted) return;
    this.initContext();
    if (!this.ctx || !this.masterGain) return;

    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(320, now);
    osc.frequency.exponentialRampToValueAtTime(80, now + 0.08);

    gain.gain.setValueAtTime(0.5, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.12);
  }

  /**
   * Triumphant harmonic victory fanfare (chords + arpeggio + shimmering bells)
   */
  public playWinnerFanfare() {
    if (this.isMuted) return;
    this.initContext();
    if (!this.ctx || !this.masterGain) return;

    const now = this.ctx.currentTime;
    
    // Notes: C5 (523.25), E5 (659.25), G5 (783.99), C6 (1046.50), E6 (1318.51), G6 (1567.98)
    const arpeggio = [
      { freq: 523.25, time: 0.00, dur: 0.22, vol: 0.35 },
      { freq: 659.25, time: 0.12, dur: 0.22, vol: 0.35 },
      { freq: 783.99, time: 0.24, dur: 0.25, vol: 0.40 },
      { freq: 1046.50, time: 0.38, dur: 1.1, vol: 0.55 },
    ];

    arpeggio.forEach(({ freq, time, dur, vol }) => {
      if (!this.ctx || !this.masterGain) return;
      const noteStart = now + time;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, noteStart);

      gain.gain.setValueAtTime(0.001, noteStart);
      gain.gain.linearRampToValueAtTime(vol, noteStart + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, noteStart + dur);

      osc.connect(gain);
      gain.connect(this.masterGain);

      osc.start(noteStart);
      osc.stop(noteStart + dur + 0.05);

      // Shimmering octave harmonic
      const harmOsc = this.ctx.createOscillator();
      const harmGain = this.ctx.createGain();
      harmOsc.type = 'sine';
      harmOsc.frequency.setValueAtTime(freq * 2, noteStart);

      harmGain.gain.setValueAtTime(0.001, noteStart);
      harmGain.gain.linearRampToValueAtTime(vol * 0.3, noteStart + 0.03);
      harmGain.gain.exponentialRampToValueAtTime(0.001, noteStart + dur * 0.8);

      harmOsc.connect(harmGain);
      harmGain.connect(this.masterGain);

      harmOsc.start(noteStart);
      harmOsc.stop(noteStart + dur);
    });

    // Sustained celebratory major chord at the finish
    const chord = [523.25, 659.25, 783.99, 1046.5];
    const chordStart = now + 0.38;
    chord.forEach((freq) => {
      if (!this.ctx || !this.masterGain) return;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, chordStart);

      gain.gain.setValueAtTime(0.001, chordStart);
      gain.gain.linearRampToValueAtTime(0.18, chordStart + 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, chordStart + 1.2);

      osc.connect(gain);
      gain.connect(this.masterGain);

      osc.start(chordStart);
      osc.stop(chordStart + 1.25);
    });
  }

  /**
   * Celebratory confetti cannon pop
   */
  public playConfettiPop() {
    if (this.isMuted) return;
    this.initContext();
    if (!this.ctx || !this.masterGain) return;

    const now = this.ctx.currentTime;
    
    // Snappy pop
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(450, now);
    osc.frequency.exponentialRampToValueAtTime(60, now + 0.09);

    gain.gain.setValueAtTime(0.4, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.11);
  }

  /**
   * Subtle modern UI button click
   */
  public playButtonClick() {
    if (this.isMuted) return;
    this.initContext();
    if (!this.ctx || !this.masterGain) return;

    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.exponentialRampToValueAtTime(300, now + 0.03);

    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.045);
  }
}

export const soundEngine = new SoundEngine();
